//app.js
import React, { useState, useEffect, useContext, useMemo } from "react";
import {
	Routes,
	Route,
	useNavigate,
	Navigate,
	useLocation,
} from "react-router-dom";
import HomePage from "./components/HomePage";
import AdminDashboard from "./components/AdminDashboard";
import GraderAdminDashboard from "./components/GraderAdminDashboard";
import NavbarMHE from "./components/NavbarMHE";
import NavbarCSX from "./components/NavbarCSX";
import DetailsPage from "./components/DetailsPage";
import useInactivityLogout from "./useInactivityLogout";
import CompetencyMatrix from "./components/CompetencyMatrix";
import { MSALContext } from "./MSALContext";
import LandingPage from "./components/LandingPage";
import CsxHomePage from "./components/CsxHomePage";
import "./App.css";
import loginImage from "./tecdev.png";
import TrainersPage from "./components/TrainersPage";
import SOPDocuments from "./components/SOPDocuments";
import ResourcesCsx from "./components/ResourcesCsx";
import WorkOrderDetailsPage from "./components/WorkOrderDetailsPage";
import SevTechsPage from "./components/SevTechsPage";
import { loginRequest } from "./Auth/msalConfig";
import TechPlan from "./components/TechPlan.js";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import axios from "axios";
import { setupApiAuth } from "./api/setupApi";
import "./theme.css";

const App = () => {
	const { msalInstance } = useContext(MSALContext);
	const [account, setAccount] = useState(null);
	const [accessToken, setAccessToken] = useState(null);
	const [permissions, setPermissions] = useState([]);
	const [isMSALReady, setIsMSALReady] = useState(false);
	const [isAdmin, setIsAdmin] = useState(false);
	const [isGrader, setIsGrader] = useState(false);
	const [permissionsFetched, setPermissionsFetched] = useState(false);
	const [isManager, setIsManager] = useState(false);
	const [isTrainer, setIsTrainer] = useState(false);
	const [isSpear, setIsSpear] = useState(false);
	const [profilePicture, setProfilePicture] = useState(null);
	const [userDisplayName, setUserDisplayName] = useState("");

	const navigate = useNavigate();
	const location = useLocation();

	const isMHE = location.pathname.startsWith("/mhe");
	const isCSX =
		location.pathname.startsWith("/csx") ||
		location.pathname === "/resourcescsx";

	const selfAlias = useMemo(() => {
		const firstWithAlias = permissions.find((p) => p?.etp_alias);
		return firstWithAlias?.etp_alias || "";
	}, [permissions]);

	const fetchUserProfile = async (token) => {
		try {
			const cachedImage = sessionStorage.getItem("profilePicture");
			const cachedDisplayName = sessionStorage.getItem("userDisplayName");

			if (cachedImage) setProfilePicture(cachedImage);

			if (cachedDisplayName) {
				setUserDisplayName(cachedDisplayName);
				return;
			}

			const profileResponse = await axios.get(
				"https://graph.microsoft.com/v1.0/me",
				{
					headers: { Authorization: `Bearer ${token}` },
				},
			);

			const displayName = profileResponse.data.displayName || "User";
			sessionStorage.setItem("userDisplayName", displayName);
			setUserDisplayName(displayName);

			const pictureResponse = await axios.get(
				"https://graph.microsoft.com/v1.0/me/photo/$value",
				{
					headers: { Authorization: `Bearer ${token}` },
					responseType: "arraybuffer",
				},
			);

			const base64Image = `data:image/jpeg;base64,${btoa(
				new Uint8Array(pictureResponse.data).reduce(
					(data, byte) => data + String.fromCharCode(byte),
					"",
				),
			)}`;

			sessionStorage.setItem("profilePicture", base64Image);
			setProfilePicture(base64Image);
		} catch (error) {
			if (error.response && error.response.status === 429) {
				console.warn("Throttled (429), not retrying.");
			} else {
				console.error("Failed to fetch profile data:", error);
			}
			setProfilePicture(null);
		}
	};

	// Initialize MSAL account ONCE
	useEffect(() => {
		const initAuth = async () => {
			try {
				await msalInstance.handleRedirectPromise();
				const accounts = msalInstance.getAllAccounts();
				if (accounts.length > 0) {
					setAccount(accounts[0]);
					setupApiAuth(accounts[0].username);
				}
			} catch (error) {
				console.error("Error handling redirect:", error);
			}
		};

		if (msalInstance) {
			initAuth();
		}
	}, [msalInstance]);

	// Acquire token for current account
	useEffect(() => {
		const fetchToken = async () => {
			try {
				if (!account) return;

				setupApiAuth(account.username);

				const tokenResponse = await msalInstance.acquireTokenSilent({
					...loginRequest,
					account,
				});

				setAccessToken(tokenResponse.accessToken);

				if (!sessionStorage.getItem("profilePicture")) {
					await fetchUserProfile(tokenResponse.accessToken);
				}
			} catch (error) {
				console.error("Error acquiring token:", error);

				const errorCode =
					error?.errorCode ||
					error?.code ||
					error?.subError ||
					error?.suberror ||
					"";

				const message = String(error?.message || error?.errorMessage || "");

				const needsInteraction =
					errorCode === "interaction_required" ||
					errorCode === "login_required" ||
					errorCode === "consent_required" ||
					message.includes("AADSTS700084") ||
					message.includes("interaction_required") ||
					message.includes("login_required");

				const interactionInProgress =
					sessionStorage.getItem("msal.interaction.status") ===
					"interaction_in_progress";

				if (
					(error instanceof InteractionRequiredAuthError || needsInteraction) &&
					!interactionInProgress
				) {
					await msalInstance.loginRedirect(loginRequest);
				}
			}
		};

		if (account && !accessToken) {
			fetchToken();
		}
	}, [account, accessToken, msalInstance]);

	// Fetch permissions once per logged-in account
	useEffect(() => {
		if (!account || permissionsFetched) return;

		const username = account.username;
		const apiBase = process.env.REACT_APP_API_URL;
		if (!apiBase) {
			console.error("REACT_APP_API_URL is not set");
			return;
		}

		setupApiAuth(username);

		const applyPermissions = (rows) => {
			setPermissions(rows);

			setIsAdmin(
				rows.some(
					(perm) =>
						String(perm.PermissionLevel || "").toLowerCase() === "admin",
				),
			);
			setIsGrader(
				rows.some(
					(perm) =>
						String(perm.PermissionLevel || "").toLowerCase() === "grader",
				),
			);
			setIsManager(
				rows.some(
					(perm) =>
						String(perm.PermissionLevel || "").toLowerCase() === "manager",
				),
			);
			setIsTrainer(
				rows.some(
					(perm) =>
						String(perm.PermissionLevel || "").toLowerCase() === "trainer",
				),
			);
			setIsSpear(
				rows.some(
					(perm) =>
						String(perm.PermissionLevel || "").toLowerCase() === "spear",
				),
			);
		};

		const cacheKey = "permissionsCache";
		const cacheRaw = sessionStorage.getItem(cacheKey);

		const fetchWithTimeout = (url, options = {}, timeoutMs = 15000) =>
			new Promise((resolve, reject) => {
				const controller = new AbortController();
				const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
				fetch(url, { ...options, signal: controller.signal })
					.then(resolve)
					.catch(reject)
					.finally(() => clearTimeout(timeoutId));
			});

		const fetchWithRetry = async (url, options = {}, retries = 2) => {
			let attempt = 0;
			while (attempt <= retries) {
				try {
					const res = await fetchWithTimeout(url, options, 15000);
					if ([408, 429, 500, 502, 503, 504].includes(res.status)) {
						if (attempt === retries) return res;
						await new Promise((r) => setTimeout(r, 400 * Math.pow(2, attempt)));
						attempt++;
						continue;
					}
					return res;
				} catch (err) {
					if (attempt === retries) throw err;
					await new Promise((r) => setTimeout(r, 400 * Math.pow(2, attempt)));
					attempt++;
				}
			}
			return null;
		};

		const run = async () => {
			try {
				const res = await fetchWithRetry(
					`${apiBase}/permissions/${encodeURIComponent(username)}`,
					{
						headers: {
							"x-trainer-id": username,
						},
					},
				);

				if (!res || !res.ok) {
					throw new Error(
						`Permissions fetch failed (${res?.status || "no response"})`,
					);
				}

				const data = await res.json();
				const rows = Array.isArray(data) ? data : [];
				applyPermissions(rows);
				sessionStorage.setItem(cacheKey, JSON.stringify(rows));
				setPermissionsFetched(true);
			} catch (error) {
				console.error("Error fetching permissions:", error);
				if (cacheRaw) {
					try {
						const cachedRows = JSON.parse(cacheRaw);
						if (Array.isArray(cachedRows) && cachedRows.length) {
							applyPermissions(cachedRows);
							setPermissionsFetched(true);
							return;
						}
					} catch (_) {}
				}
			}
		};

		run();
	}, [account, permissionsFetched]);

	useEffect(() => {
		if (!account || !permissionsFetched) return;

		// Admins/managers should not be auto-redirected into a trainee details view.
		if (isAdmin || isManager) return;

		if (location.pathname === "/mhe-home" && selfAlias) {
			navigate(`/details/${selfAlias}`, { replace: true });
		}
	}, [
		account,
		permissionsFetched,
		isAdmin,
		isManager,
		selfAlias,
		location.pathname,
		navigate,
	]);

	const handleSignIn = async () => {
		try {
			const interactionInProgress =
				sessionStorage.getItem("msal.interaction.status") ===
				"interaction_in_progress";

			if (!interactionInProgress) {
				await msalInstance.loginRedirect(loginRequest);
			}
		} catch (error) {
			console.error("Error during login:", error);
		}
	};

	const handleSignOut = async () => {
		try {
			await msalInstance.logoutRedirect({
				postLogoutRedirectUri: "/",
			});
		} catch (error) {
			console.error("Error during logout:", error);
		}
	};

	useEffect(() => {
		setIsMSALReady(true);
	}, []);

	useInactivityLogout(msalInstance, handleSignOut);

	return (
		<div className="app">
			{!account && isMSALReady ? (
				<div className="login-container">
					<img src={loginImage} alt="Login" />
					<h3>Electronic Training Plan</h3>
					<button onClick={handleSignIn}>Sign In</button>
				</div>
			) : (
				<>
					<div className="background-image"></div>

					{location.pathname !== "/" &&
						(isMHE ? (
							<NavbarMHE
								trainerID={account?.username || ""}
								onSignOut={handleSignOut}
								isAdmin={isAdmin}
								isGrader={isGrader}
								accessToken={accessToken}
								profilePicture={profilePicture}
								userDisplayName={userDisplayName}
								isManager={isManager}
							/>
						) : isCSX ? (
							<NavbarCSX
								trainerID={account?.username || ""}
								onSignOut={handleSignOut}
							/>
						) : (
							<NavbarMHE
								trainerID={account?.username || ""}
								onSignOut={handleSignOut}
								isAdmin={isAdmin}
								isGrader={isGrader}
								isManager={isManager}
							/>
						))}

					<div className="container mt-3">
						<Routes>
							<Route
								path="/"
								element={
									<LandingPage
										isAdmin={isAdmin}
										isManager={isManager}
										isTrainer={isTrainer}
										isSpear={isSpear}
										trainerID={account?.username || ""}
										selfAlias={selfAlias}
										permissionsFetched={permissionsFetched}
									/>
								}
							/>

							<Route
								path="/mhe-home"
								element={
									!permissionsFetched ? (
										<div className="text-center py-4">Loading...</div>
									) : isAdmin || isManager ? (
										<HomePage
											trainerID={account?.username || ""}
											isAdmin={isAdmin}
											isManager={isManager}
										/>
									) : selfAlias ? (
										<Navigate to={`/details/${selfAlias}`} replace />
									) : (
										<Navigate to="/" replace />
									)
								}
							/>

							<Route path="/competency-matrix" element={<CompetencyMatrix />} />
							<Route path="/tech-plan" element={<TechPlan />} />
							<Route path="/trainers" element={<TrainersPage />} />

							<Route
								path="/details/:alias"
								element={
									<DetailsPage
										trainerID={account?.username || ""}
										isManager={isManager}
										isAdmin={isAdmin}
										isTrainer={isTrainer}
										isSpear={isSpear}
									/>
								}
							/>

							<Route
								path="/work-order/:alias/:coreCompCode"
								element={
									<WorkOrderDetailsPage trainerID={account?.username || ""} />
								}
							/>

							<Route
								path="/sop-documents"
								element={<SOPDocuments isAdmin={isAdmin} />}
							/>

							<Route
								path="/admin-dashboard"
								element={
									isAdmin ? (
										<AdminDashboard trainerID={account?.username || ""} />
									) : (
										<Navigate to="/" />
									)
								}
							/>

							<Route
								path="/grader-admin-dashboard"
								element={
									isAdmin || isGrader ? (
										<GraderAdminDashboard trainerID={account?.username || ""} />
									) : (
										<Navigate to="/" />
									)
								}
							/>

							<Route
								path="/sev-techs"
								element={
									<SevTechsPage
										trainerID={account?.username || ""}
										isAdmin={isAdmin}
										isManager={isManager}
									/>
								}
							/>

							<Route path="/csx-home" element={<CsxHomePage />} />
							<Route path="/resourcescsx" element={<ResourcesCsx />} />
							<Route path="*" element={<Navigate to="/" />} />
						</Routes>
					</div>
				</>
			)}
		</div>
	);
};

export default App;
